import type { ProColumns } from "@ant-design/pro-components";
import { Menu, Space, Tag, Typography } from "antd";
import { useMemo, useState } from "react";
import { buildTextHeaderFilter, readTableFilterValue } from "./admin-list-filters";
import { ADMIN_LIST_PAGINATION_STANDARD, readAdminStandardPageSize } from "./admin-table";
import { PickerDisplayInput, PickerInlineControl } from "./picker-form-field";
import { CatalogPickerModal } from "./catalog-picker-modal";

type PickerTableFilters = Record<string, (string | number | boolean)[] | null | undefined>;
export type KsaCategoryType = "knowledge" | "skill" | "ability";

export type KsaCategoryPickerCategory = {
  id: number;
  category_key: string;
  name: string;
  ksa_type: string;
  level: string;
  status: string;
  description?: string | null;
};

export type KsaCategoryBinding = {
  category_id: number;
  category_key: string;
  name: string;
  code: string;
  label: string;
  ksa_type: string;
  level: string;
};

export type KsaCategoryPickerMessages = {
  placeholder: string;
  pickButtonText: string;
  clearButtonText: string;
  knowledgeGroupTitle: string;
  skillGroupTitle: string;
  abilityGroupTitle: string;
  modalTitle: string;
  modalSubmitText: string;
  modalCancelText: string;
  emptyText: string;
  nameColumnTitle: string;
  keyColumnTitle: string;
  typeColumnTitle: string;
  levelColumnTitle: string;
  statusColumnTitle: string;
  searchNamePlaceholder: string;
  searchKeyPlaceholder: string;
};

export type KsaCategoryPickerListParams = {
  page: number;
  pageSize: number;
  keyword?: string;
  ksaType?: KsaCategoryType;
  status: string;
};

export type KsaCategoryPickerListResponse = {
  items: KsaCategoryPickerCategory[];
  total: number;
};

export type KsaCategoryPickerFieldProps = {
  value?: KsaCategoryBinding[];
  onChange?: (value: KsaCategoryBinding[]) => void;
  listCategories: (params: KsaCategoryPickerListParams) => Promise<KsaCategoryPickerListResponse>;
  messages: KsaCategoryPickerMessages;
  persistenceKey: string;
  selectionMode?: "single" | "multiple";
  fixedKsaType?: KsaCategoryType;
  disabled?: boolean;
};

function normalizeKsaType(value: string | null | undefined): KsaCategoryType | null {
  if (value === "knowledge" || value === "skill" || value === "ability") {
    return value;
  }
  return null;
}

function requireKsaType(value: string | null | undefined, source: string): KsaCategoryType {
  const normalized = normalizeKsaType(value);
  if (!normalized) {
    throw new Error(`${source} must be knowledge, skill, or ability`);
  }
  return normalized;
}

function resolveInitialActiveKsaType(
  bindings: KsaCategoryBinding[],
  fixedKsaType?: KsaCategoryType,
): KsaCategoryType {
  if (fixedKsaType) {
    const requiredFixedKsaType = requireKsaType(fixedKsaType, "Fixed KSA category type");
    const mismatchedBinding = bindings.find((binding) => binding.ksa_type !== fixedKsaType);
    if (mismatchedBinding) {
      throw new Error(
        `KSA category binding ${mismatchedBinding.category_id} must have fixed type ${fixedKsaType}`,
      );
    }
    return requiredFixedKsaType;
  }
  if (bindings.length > 0) {
    const firstBinding = bindings[0];
    if (!firstBinding) throw new Error("KSA category binding is missing");
    return requireKsaType(firstBinding.ksa_type, "KSA category binding type");
  }
  return "knowledge";
}

function normalizeCategoryBinding(category: KsaCategoryPickerCategory): KsaCategoryBinding {
  return {
    category_id: category.id,
    category_key: category.category_key,
    name: category.name,
    code: category.category_key,
    label: category.name,
    ksa_type: category.ksa_type,
    level: category.level,
  };
}

function bindingToCategory(binding: KsaCategoryBinding): KsaCategoryPickerCategory {
  return {
    id: binding.category_id,
    category_key: binding.category_key,
    name: binding.name || binding.label,
    ksa_type: binding.ksa_type,
    level: binding.level,
    status: "active",
  };
}

function buildDisplayText(value: KsaCategoryBinding[] | undefined) {
  return (value ?? [])
    .map((item) => {
      const name = String(item.name || item.label || "").trim();
      const key = String(item.category_key || item.code || "").trim();
      if (name && key) {
        return `${name} (${key})`;
      }
      return name || key;
    })
    .filter(Boolean)
    .join(", ");
}

export function KsaCategoryPickerField({
  value,
  onChange,
  listCategories,
  messages,
  persistenceKey,
  selectionMode = "multiple",
  fixedKsaType,
  disabled = false,
}: KsaCategoryPickerFieldProps) {
  const isSingleSelection = selectionMode === "single";
  const selectedBindings = isSingleSelection ? (value ?? []).slice(0, 1) : (value ?? []);
  const [open, setOpen] = useState(false);
  const [activeKsaType, setActiveKsaType] = useState<KsaCategoryType>(
    resolveInitialActiveKsaType(selectedBindings, fixedKsaType),
  );
  const [draftKeys, setDraftKeys] = useState<Array<string | number>>([]);
  const [draftCategoryMap, setDraftCategoryMap] = useState<Map<number, KsaCategoryPickerCategory>>(
    new Map(),
  );

  const columns = useMemo<ProColumns<KsaCategoryPickerCategory>[]>(
    () => [
      {
        title: messages.nameColumnTitle,
        dataIndex: "name",
        ellipsis: true,
        ...buildTextHeaderFilter(messages.searchNamePlaceholder),
        render: (_, record) => (
          <Space orientation="vertical" size={2}>
            <Typography.Text strong>{record.name}</Typography.Text>
            {record.description ? (
              <Typography.Text type="secondary">{record.description}</Typography.Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: messages.keyColumnTitle,
        dataIndex: "category_key",
        width: 180,
        ...buildTextHeaderFilter(messages.searchKeyPlaceholder),
      },
      {
        title: messages.typeColumnTitle,
        dataIndex: "ksa_type",
        width: 120,
        render: (_, record) => <Tag>{record.ksa_type}</Tag>,
      },
      {
        title: messages.levelColumnTitle,
        dataIndex: "level",
        width: 120,
        render: (_, record) => <Tag>{record.level}</Tag>,
      },
      {
        title: messages.statusColumnTitle,
        dataIndex: "status",
        width: 120,
      },
    ],
    [messages],
  );

  const openPicker = () => {
    setDraftKeys(selectedBindings.map((item) => item.category_id));
    setDraftCategoryMap(
      new Map(selectedBindings.map((item) => [item.category_id, bindingToCategory(item)])),
    );
    setActiveKsaType(resolveInitialActiveKsaType(selectedBindings, fixedKsaType));
    setOpen(true);
  };

  const groupItems = useMemo(
    () => [
      { key: "knowledge", label: messages.knowledgeGroupTitle },
      { key: "skill", label: messages.skillGroupTitle },
      { key: "ability", label: messages.abilityGroupTitle },
    ],
    [messages.abilityGroupTitle, messages.knowledgeGroupTitle, messages.skillGroupTitle],
  );

  const applySelection = async () => {
    const selectedCategories = draftKeys
      .map((key) => draftCategoryMap.get(Number(key)))
      .filter((item): item is KsaCategoryPickerCategory => item !== undefined);
    if (fixedKsaType) {
      const mismatchedCategory = selectedCategories.find(
        (category) =>
          requireKsaType(category.ksa_type, "Selected KSA category type") !== fixedKsaType,
      );
      if (mismatchedCategory) {
        throw new Error(
          `Selected KSA category ${mismatchedCategory.id} must have fixed type ${fixedKsaType}`,
        );
      }
    }
    const selected = selectedCategories.map(normalizeCategoryBinding);
    onChange?.(selected);
    setOpen(false);
    return true;
  };

  return (
    <>
      <PickerInlineControl
        control={
          <PickerDisplayInput
            disabled={disabled}
            value={buildDisplayText(selectedBindings)}
            placeholder={messages.placeholder}
          />
        }
        pickButtonText={messages.pickButtonText}
        onPick={openPicker}
        pickButtonProps={{ disabled }}
        clearButtonText={messages.clearButtonText}
        onClear={() => {
          if (!disabled && selectedBindings.length > 0) {
            onChange?.([]);
          }
        }}
        clearButtonProps={{ disabled: disabled || selectedBindings.length === 0 }}
      />
      <CatalogPickerModal<KsaCategoryPickerCategory>
        title={messages.modalTitle}
        submitText={messages.modalSubmitText}
        cancelText={messages.modalCancelText}
        open={open}
        onOpenChange={setOpen}
        columns={columns}
        persistenceKey={persistenceKey}
        rowKey="id"
        selectedRowKeys={draftKeys}
        onSelectionChange={(keys, rows) => {
          const nextKeys = isSingleSelection ? keys.slice(-1) : keys;
          const nextRows = isSingleSelection ? rows.slice(-1) : rows;
          setDraftKeys(nextKeys);
          setDraftCategoryMap((current) => {
            const next = new Map(current);
            for (const row of nextRows) {
              next.set(row.id, row);
            }
            return next;
          });
        }}
        emptyText={messages.emptyText}
        rowSelectionType={isSingleSelection ? "radio" : "checkbox"}
        {...(fixedKsaType
          ? {}
          : {
              sidebar: (
            <div
              style={{
                border: "1px solid var(--ant-colorBorderSecondary, #f0f0f0)",
                borderRadius: 8,
                padding: 8,
                background: "var(--ant-colorBgContainer, #fff)",
              }}
            >
              <Menu
                mode="inline"
                selectedKeys={[activeKsaType]}
                items={groupItems}
                onClick={({ key }) => {
                  const nextType = normalizeKsaType(String(key));
                  if (nextType) {
                    setActiveKsaType(nextType);
                  }
                }}
                style={{ borderInlineEnd: "none", background: "transparent" }}
              />
            </div>
              ),
            })}
        {...(fixedKsaType ? {} : { sidebarWidth: 208 })}
        width={1120}
        onSubmit={applySelection}
        tableProps={{
          params: { activeKsaType },
          request: async (params, _sorter, filters) => {
            const normalizedFilters = filters as PickerTableFilters;
            const nameKeyword = readTableFilterValue(normalizedFilters, "name") || "";
            const keyKeyword = readTableFilterValue(normalizedFilters, "category_key") || "";
            const response = await listCategories({
              page: Math.max(1, Number(params.current || 1)),
              pageSize: readAdminStandardPageSize(params.pageSize),
              ...((nameKeyword || keyKeyword) ? { keyword: nameKeyword || keyKeyword } : {}),
              ksaType: fixedKsaType ?? activeKsaType,
              status: "active",
            });
            const requestedKsaType = fixedKsaType ?? activeKsaType;
            const mismatchedCategory = response.items.find(
              (category) =>
                requireKsaType(category.ksa_type, "KSA category response type") !==
                requestedKsaType,
            );
            if (mismatchedCategory) {
              throw new Error(
                `KSA category ${mismatchedCategory.id} must have requested type ${requestedKsaType}`,
              );
            }
            return {
              data: response.items,
              success: true,
              total: response.total,
            };
          },
          pagination: ADMIN_LIST_PAGINATION_STANDARD,
        }}
      />
    </>
  );
}
